/**
 * OIMS v2 - Windows IIS tab (Command Bridge)
 * Instance/port selection + SSE streaming for IIS management
 */

import { escapeHtml, clearLog, appendLog } from './utils.js';

let selWindowsInstance, selPort, selWindowsAction, btnExecuteWindows, windowsLogArea;
let windowsInstances = [];
let currentWindowsExecMeta = null;
let windowsSse = null;
let windowsSseEnded = false;
let windowsSseRetryCount = 0;

// Recent operations history (session-only, max 10)
const recentOps = [];
const MAX_RECENT_OPS = 10;

export function initWindows(state) {
  selWindowsInstance = document.getElementById("selWindowsInstance");
  selPort = document.getElementById("selPort");
  selWindowsAction = document.getElementById("selWindowsAction");
  btnExecuteWindows = document.getElementById("btnExecuteWindows");
  windowsLogArea = document.getElementById("windowsLogArea");

  if (!selWindowsInstance) return;

  selWindowsInstance.addEventListener("change", () => {
    handleWindowsInstanceChange();
    updateBriefing();
  });
  selPort && selPort.addEventListener("change", () => {
    handlePortChange();
    updateBriefing();
  });
  btnExecuteWindows && btnExecuteWindows.addEventListener("click", () => confirmAndExecuteWindows(state));

  // Terminal toolbar
  const btnClear = document.getElementById("btnClearWindowsLog");
  const btnCopy = document.getElementById("btnCopyWindowsLog");
  if (btnClear) btnClear.addEventListener("click", () => clearLog(windowsLogArea));
  if (btnCopy) btnCopy.addEventListener("click", () => copyLogToClipboard());

  // Start streaming after approval/break-glass execution for IIS actions
  document.addEventListener('oims:approval-executed', (e) => {
    const { command_id, instance_id, instance_name, action_id, request_id } = e.detail || {};
    if (!command_id || !instance_id) return;
    // Only handle IIS actions in this module
    if (action_id !== 'restart_iis_apppool') return;
    const display_name = instance_name || instance_id;
    currentWindowsExecMeta = { instance_id, command_id, action_id, request_id, display_name };
    if (windowsLogArea) {
      clearLog(windowsLogArea);
      appendLog(windowsLogArea, `[Approved] Streaming output for ${display_name}...`);
    }
    setLaunchState(true);
    startWindowsStream(state);
  });
}

export async function loadWindowsInstances(state) {
  if (!state.accessToken) return;
  selWindowsInstance.innerHTML = '<option value="">Loading instances...</option>';
  selWindowsInstance.disabled = true;
  try {
    const { data } = await axios.get('/actions/instances?action_id=restart_iis_apppool');
    windowsInstances = data.instances || [];

    selWindowsInstance.innerHTML = '<option value="">Select instance...</option>';
    windowsInstances.forEach(inst => {
      const opt = document.createElement('option');
      opt.value = inst.instance_id;

      let displayName = inst.name;
      if (inst.ip) {
        const lastOctet = inst.ip.split('.').pop();
        displayName = `${inst.name}.${lastOctet}`;
      }

      opt.textContent = displayName;
      opt.dataset.ports = JSON.stringify(inst.ports || []);
      selWindowsInstance.appendChild(opt);
    });
  } catch (e) {
    console.error('Failed to load Windows instances:', e);
    selWindowsInstance.innerHTML = '<option value="">Failed to load instances</option>';
  } finally {
    selWindowsInstance.disabled = false;
  }
}

function handleWindowsInstanceChange() {
  const selectedOption = selWindowsInstance.options[selWindowsInstance.selectedIndex];

  if (!selectedOption || !selectedOption.value) {
    selPort.disabled = true;
    selPort.innerHTML = '<option value="">Select instance first...</option>';
    btnExecuteWindows.disabled = true;
    return;
  }

  const ports = JSON.parse(selectedOption.dataset.ports || '[]');

  selPort.disabled = false;
  selPort.innerHTML = '<option value="">Select port...</option>';
  ports.forEach(port => {
    const opt = document.createElement('option');
    opt.value = port;
    opt.textContent = `Port ${port}`;
    selPort.appendChild(opt);
  });

  btnExecuteWindows.disabled = true;
}

function handlePortChange() {
  const ready = selWindowsInstance.value && selPort.value;
  btnExecuteWindows.disabled = !ready;
}

function setWindowsStatus(text, color = "secondary") {
  const badge = document.getElementById("windowsStatusBadge");
  if (!badge) return;
  badge.textContent = text;
  const variantMap = { secondary: "idle", info: "running", success: "success", danger: "error", primary: "running", warning: "running" };
  const variant = variantMap[color] || "idle";
  badge.className = `status-badge ${variant}`;
}

function updateBriefing() {
  const briefingText = document.getElementById("windowsBriefingText");
  if (!briefingText) return;

  const instanceLabel = selWindowsInstance?.options[selWindowsInstance.selectedIndex]?.textContent?.trim();
  const portLabel = selPort?.options[selPort.selectedIndex]?.textContent?.trim();
  const hasInstance = selWindowsInstance?.value;
  const hasPort = selPort?.value;

  if (hasInstance && hasPort) {
    briefingText.innerHTML = `<span class="briefing-ready"><i class="bi bi-check-circle me-1"></i>Ready: <strong>Restart IIS App Pool</strong> on <strong>${escapeHtml(instanceLabel)} → ${escapeHtml(portLabel)}</strong></span>`;
  } else if (hasInstance) {
    briefingText.textContent = `Instance: ${instanceLabel} — select a service port`;
  } else {
    briefingText.textContent = "Select an instance and port to begin";
  }

  // Update launch button subtitle
  const launchSmall = btnExecuteWindows?.querySelector(".launch-text small");
  if (launchSmall) {
    if (hasInstance && hasPort) {
      launchSmall.textContent = `${instanceLabel} → ${portLabel}`;
    } else {
      launchSmall.textContent = "Select instance & port first";
    }
  }
}

function copyLogToClipboard() {
  if (!windowsLogArea) return;
  const text = windowsLogArea.innerText || windowsLogArea.textContent || "";
  if (!text.trim()) {
    window.toast?.info("Nothing to copy.");
    return;
  }
  navigator.clipboard.writeText(text).then(() => {
    window.toast?.success("Output copied to clipboard.");
  }).catch(() => {
    window.toast?.error("Failed to copy.");
  });
}

function showExecMetadata(instanceLabel, portLabel) {
  const meta = document.getElementById("windowsExecMetadata");
  const metaInstance = document.getElementById("windowsMetaInstance");
  const metaPort = document.getElementById("windowsMetaPort");
  const metaTime = document.getElementById("windowsMetaTime");
  if (meta) {
    meta.style.display = "";
    if (metaInstance) metaInstance.textContent = instanceLabel;
    if (metaPort) metaPort.textContent = portLabel;
    if (metaTime) metaTime.textContent = new Date().toLocaleTimeString();
  }
}

function setLaunchState(executing) {
  if (!btnExecuteWindows) return;
  if (executing) {
    btnExecuteWindows.disabled = true;
    btnExecuteWindows.classList.add("executing");
    const icon = btnExecuteWindows.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-arrow-repeat";
    const strong = btnExecuteWindows.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Executing...";
  } else {
    btnExecuteWindows.disabled = false;
    btnExecuteWindows.classList.remove("executing");
    const icon = btnExecuteWindows.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-power";
    const strong = btnExecuteWindows.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Execute Restart";
    updateBriefing();
  }
}

function addRecentOp(targetName, status) {
  recentOps.unshift({
    action: "Restart IIS App Pool",
    target: targetName,
    status,
    time: new Date(),
  });
  if (recentOps.length > MAX_RECENT_OPS) recentOps.pop();
  renderRecentOps();
}

function renderRecentOps() {
  const wrap = document.getElementById("windowsRecentOps");
  const list = document.getElementById("windowsRecentOpsList");
  if (!wrap || !list) return;

  if (recentOps.length === 0) {
    wrap.style.display = "none";
    return;
  }

  wrap.style.display = "";
  list.innerHTML = "";

  recentOps.forEach(op => {
    const statusIcon = op.status === "success" ? "bi-check-circle-fill" : op.status === "error" ? "bi-x-circle-fill" : op.status === "pending" ? "bi-shield-lock-fill" : "bi-arrow-repeat";
    const statusClass = op.status === "success" ? "op-success" : op.status === "error" ? "op-error" : op.status === "pending" ? "op-pending" : "op-running";
    const timeStr = op.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const item = document.createElement("div");
    item.className = "recent-op-item";
    item.innerHTML = `
      <div class="recent-op-left">
        <span class="recent-op-icon ${statusClass}"><i class="bi ${statusIcon}"></i></span>
        <span class="recent-op-name">${escapeHtml(op.action)}</span>
        <span class="recent-op-target">${escapeHtml(op.target)}</span>
      </div>
      <span class="recent-op-time">${timeStr}</span>`;
    list.appendChild(item);
  });
}

function confirmAndExecuteWindows(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to execute actions.");
    return;
  }
  const instance_id = selWindowsInstance.value;
  const port = selPort.value;
  if (!instance_id || !port) {
    window.toast.warning("Please select instance and port.");
    return;
  }

  const instanceLabel = selWindowsInstance.options[selWindowsInstance.selectedIndex]?.textContent?.trim() || instance_id;
  const portLabel = selPort.options[selPort.selectedIndex]?.textContent?.trim() || port;

  document.getElementById("confirmActionName").textContent = "Restart IIS App Pool";
  document.getElementById("confirmInstanceName").textContent = `${instanceLabel} → Port ${port}`;

  const approvalHint = document.getElementById("confirmApprovalHint");
  if (approvalHint) approvalHint.style.display = 'none';

  const modalEl = document.getElementById("confirmActionModal");
  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  const btnConfirm = document.getElementById("btnConfirmExecute");
  const newBtn = btnConfirm.cloneNode(true);
  btnConfirm.parentNode.replaceChild(newBtn, btnConfirm);
  newBtn.addEventListener("click", () => {
    modal.hide();
    executeWindowsAction(state);
  });
}

async function executeWindowsAction(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to execute actions.");
    return;
  }
  const instance_id = selWindowsInstance.value;
  const port = selPort.value;
  const action_id = selWindowsAction.value;

  if (!instance_id || !port || !action_id) {
    window.toast.warning("Please select instance, port, and action.");
    return;
  }

  const instanceLabel = selWindowsInstance.options[selWindowsInstance.selectedIndex]?.textContent?.trim() || instance_id;
  const displayName = `${instanceLabel}:${port}`;

  setLaunchState(true);

  if (windowsSse) stopWindowsStream();
  clearLog(windowsLogArea);
  setWindowsStatus("Starting...", "secondary");
  showExecMetadata(instanceLabel, `Port ${port}`);
  appendLog(windowsLogArea, `Executing: Restart IIS App Pool on ${displayName}...`);

  try {
    const url = `/actions/execute?action_id=${encodeURIComponent(action_id)}&instance_id=${encodeURIComponent(instance_id)}&port=${encodeURIComponent(port)}`;
    const { data } = await axios.post(url);

    // Maker/Checker: action requires approval
    if (data.status === "approval_required") {
      setWindowsStatus("Approval Pending", "warning");
      appendLog(windowsLogArea, `\u23f3 ${data.message || 'Approval required for this action.'}`);
      setLaunchState(false);
      addRecentOp(displayName, "pending");
      return;
    }

    if (data.status !== "sent") {
      setWindowsStatus("Error", "danger");
      appendLog(windowsLogArea, "ERROR: " + (data.message || "unknown"));
      setLaunchState(false);
      addRecentOp(displayName, "error");
      return;
    }

    currentWindowsExecMeta = { instance_id, command_id: data.command_id, action_id, display_name: displayName };
    appendLog(windowsLogArea, `SSM command sent. Command ID: ${data.command_id}`);
    startWindowsStream(state);
  } catch (e) {
    setWindowsStatus("Error", "danger");
    appendLog(windowsLogArea, "Execution error: " + (e.response?.data?.detail || e.message));
    setLaunchState(false);
    addRecentOp(displayName, "error");
  }
}

function startWindowsStream(state) {
  if (!currentWindowsExecMeta || !state.accessToken) {
    window.toast.warning("No command to stream.");
    return;
  }
  if (windowsSse && windowsSse.readyState !== EventSource.CLOSED) {
    appendLog(windowsLogArea, "[SSE] Stream already running; ignoring second start.");
    return;
  }

  windowsSseEnded = false;

  const { instance_id, command_id, action_id, display_name, request_id } = currentWindowsExecMeta;
  const url = new URL("/actions/stream", window.location.origin);
  url.searchParams.set("instance_id", instance_id);
  url.searchParams.set("command_id", command_id);
  url.searchParams.set("access_token", state.accessToken);
  if (action_id) url.searchParams.set("action_id", action_id);
  if (display_name) url.searchParams.set("display_name", display_name);
  if (request_id) url.searchParams.set("request_id", request_id);

  windowsSse = new EventSource(url.toString());
  appendLog(windowsLogArea, "[SSE] Opened stream...");
  setWindowsStatus("Streaming", "primary");

  windowsSse.onopen = () => {
    windowsSseRetryCount = 0;
    setWindowsStatus("Streaming", "primary");
  };

  windowsSse.addEventListener("message", (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.chunk) appendLog(windowsLogArea, msg.chunk);
      if (msg.finished) {
        appendLog(windowsLogArea, "\n────────────────────────────────────");
        appendLog(windowsLogArea, "[SSE] Command completed.");
        setWindowsStatus("Complete", "success");
        windowsSseEnded = true;
        windowsSse.close();
        setLaunchState(false);
        addRecentOp(display_name, "success");
      }
    } catch {
      if (ev.data) appendLog(windowsLogArea, ev.data);
    }
  });

  windowsSse.addEventListener("done", () => {
    appendLog(windowsLogArea, "\n────────────────────────────────────");
    appendLog(windowsLogArea, "[SSE] Command finished.");
    setWindowsStatus("Done", "success");
    windowsSseEnded = true;
    windowsSse.close();
    setLaunchState(false);
    addRecentOp(display_name, "success");
    setTimeout(stopWindowsStream, 120);
  });

  windowsSse.addEventListener("error", () => {
    if (windowsSseEnded) return;
    stopWindowsStream();
    windowsSseRetryCount++;
    if (windowsSseRetryCount <= 3 && currentWindowsExecMeta) {
      const delay = Math.min(1000 * Math.pow(2, windowsSseRetryCount - 1), 8000);
      setWindowsStatus("Reconnecting...", "warning");
      appendLog(windowsLogArea, `[SSE] Connection lost. Retrying in ${delay / 1000}s (attempt ${windowsSseRetryCount}/3)...`);
      setTimeout(() => startWindowsStream(state), delay);
    } else {
      setWindowsStatus("Error", "danger");
      appendLog(windowsLogArea, "[SSE] Connection failed after retries.");
      setLaunchState(false);
      addRecentOp(currentWindowsExecMeta?.display_name || "Instance", "error");
      windowsSseRetryCount = 0;
    }
  });
}

function stopWindowsStream() {
  if (windowsSse) {
    windowsSse.close();
    windowsSse = null;
  }
}
